import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Footer from "@/components/Footer";
import { useDynamicTheme } from "@/hooks/use-dynamic-theme";
import { useFaviconTitle } from "@/hooks/use-favicon-title";
import EditableSection from "@/components/editor/EditableSection";
import EditModeToolbar from "@/components/editor/EditModeToolbar";
import EditSidePanel from "@/components/editor/EditSidePanel";
import { useEditMode } from "@/contexts/EditModeContext";
import { useMemo, Suspense } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazy-with-retry";
import SeoSchemas from "@/components/SeoSchemas";
import WhatsAppFloat from "@/components/WhatsAppFloat";

// Lazy load below-fold sections to reduce initial JS bundle
const BookingSection = lazy(() => import("@/components/BookingSection"));
const Services = lazy(() => import("@/components/Services"));
const About = lazy(() => import("@/components/About"));
const Testimonials = lazy(() => import("@/components/Testimonials"));
const Contact = lazy(() => import("@/components/Contact"));

const SECTION_COMPONENTS: Record<string, React.LazyExoticComponent<React.ComponentType>> = {
  booking: BookingSection,
  services: Services,
  about: About,
  testimonials: Testimonials,
  contact: Contact,
};

const Index = () => {
  useDynamicTheme();
  useFaviconTitle();
  const { sectionOrder, isEditMode } = useEditMode();

  const visibleSections = useMemo(
    () => sectionOrder.filter((s) => s.visible || isEditMode),
    [sectionOrder, isEditMode]
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main id="main-content">
        {visibleSections.map((section) => {
          // Hero is eagerly loaded (above the fold / LCP)
          if (section.id === "hero") {
            return (
              <EditableSection key="hero" id="hero">
                <div className={!section.visible && isEditMode ? "opacity-40" : ""}>
                  <Hero />
                </div>
              </EditableSection>
            );
          }

          const Component = SECTION_COMPONENTS[section.id];
          if (!Component) return null;
          return (
            <EditableSection key={section.id} id={section.id}>
              <div className={!section.visible && isEditMode ? "opacity-40" : ""}>
                <Suspense fallback={<div className="min-h-[200px]" />}>
                  <Component />
                </Suspense>
              </div>
            </EditableSection>
          );
        })}
      </main>
      <Footer />
      <SeoSchemas />
      <WhatsAppFloat />
      <EditModeToolbar />
      <EditSidePanel />
    </div>
  );
};

export default Index;
